import { $, el, on } from "./dom";

/** ⌘K / Ctrl+K command palette. View-page actions delegate to the toolbar buttons. */

interface Action {
  label: string;
  hint?: string;
  run: () => void;
}

function clickAction(name: string): () => void {
  return () => $(`[data-action="${name}"]`)?.click();
}

function buildActions(): Action[] {
  const actions: Action[] = [{ label: "New paste", hint: "go", run: () => location.assign("/") }];

  if ($("#paste")) {
    actions.push(
      { label: "Copy code", run: clickAction("copy") },
      { label: "Open raw", run: clickAction("raw") },
      { label: "Download", run: clickAction("download") },
      { label: "Toggle word wrap", run: clickAction("wrap") },
      { label: "Find in paste", run: clickAction("search") },
      { label: "Share", run: clickAction("share") },
    );
    if ($('[data-action="markdown"]')) {
      actions.push({ label: "Toggle Markdown preview", run: clickAction("markdown") });
    }
    if ($('[data-action="diff"]')) actions.push({ label: "Toggle diff", run: clickAction("diff") });
  }

  if ($("#composer")) {
    actions.push({ label: "Focus editor", run: () => $("#paste-content")?.focus() });
    actions.push({ label: "Toggle options", run: () => $("#options-toggle")?.click() });
  }

  return actions;
}

export function initPalette(): void {
  let open = false;
  let actions: Action[] = [];
  let filtered: Action[] = [];
  let active = 0;

  const input = el("input", {
    class: "palette-input",
    type: "text",
    placeholder: "Type a command…",
  });
  const list = el("ul", { class: "palette-list" });
  const panel = el("div", { class: "palette" }, [input, list]);
  const backdrop = el("div", { class: "palette-backdrop", hidden: "" }, [panel]);
  document.body.append(backdrop);

  function render(): void {
    list.replaceChildren();
    filtered.forEach((action, i) => {
      const item = el("li", { class: `palette-item${i === active ? " active" : ""}` }, [
        el("span", {}, [action.label]),
        action.hint ? el("span", { class: "palette-hint" }, [action.hint]) : el("span", {}),
      ]);
      on(item, "click", () => run(action));
      list.append(item);
    });
  }

  function filter(query: string): void {
    const q = query.toLowerCase().trim();
    filtered = q ? actions.filter((a) => a.label.toLowerCase().includes(q)) : actions;
    active = 0;
    render();
  }

  function show(): void {
    actions = buildActions();
    filtered = actions;
    active = 0;
    input.value = "";
    render();
    backdrop.removeAttribute("hidden");
    open = true;
    input.focus();
  }

  function hide(): void {
    backdrop.setAttribute("hidden", "");
    open = false;
  }

  function run(action: Action): void {
    hide();
    action.run();
  }

  on(input, "input", () => filter(input.value));
  on(input, "keydown", (e) => {
    const ev = e as KeyboardEvent;
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      active = Math.min(active + 1, filtered.length - 1);
      render();
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      active = Math.max(active - 1, 0);
      render();
    } else if (ev.key === "Enter") {
      ev.preventDefault();
      const action = filtered[active];
      if (action) run(action);
    }
  });
  on(backdrop, "click", (e) => {
    if (e.target === backdrop) hide();
  });
  on(document, "keydown", (e) => {
    const ev = e as KeyboardEvent;
    if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "k") {
      ev.preventDefault();
      open ? hide() : show();
    } else if (ev.key === "Escape" && open) {
      hide();
    }
  });
}
